/**
 * Placeholders de imagen.
 *
 * Un único lugar para saber qué se muestra cuando a un producto o a una
 * colección le falta la foto. **No** usar paths escritos a mano: el directorio
 * `/assets/img/` del storefront vanilla **no existe** en `frontend/public/`
 * (vive solo en `frontend/legacy/dist/`, que es un artefacto de build), así
 * que cualquier path de esa forma da 404.
 *
 * Los productos y las colecciones reales usan rutas que llegan de la API:
 * `/media/productos/<slug>-<n>.webp`.
 *
 * Para el fallback se usa un **elemento del manual de marca**, que sí existe
 * en `public/brand/elementos/` y está en el manifiesto de
 * `GET /api/config/marca`. Es un gráfico de la marca, no una foto: sirve
 * para que la ficha no se rompa, no para representar la pieza.
 */

import type { Coleccion, Producto } from './tipos';

/**
 * Elemento del manual usado cuando un producto no tiene imagen.
 *
 * `trama-aros` es una trama de aros del manual de marca: es el gráfico
 * genérico de la marca y no pretende ser la foto de ninguna pieza.
 */
export const PLACEHOLDER_PRODUCTO = '/brand/elementos/trama-aros.webp';

/**
 * Elemento del manual usado cuando una colección no tiene imagen.
 */
export const PLACEHOLDER_COLECCION = '/brand/elementos/trama-espirales.webp';

/**
 * ¿El frontend sirve ese path?
 *
 * `public/` es la raíz estática de Next: `/media/productos/...` (las fotos
 * reales del catálogo) y `/brand/...` (los assets de la marca). `/assets/`
 * pertenece al build del legacy y no existe en esta app.
 *
 * Sirve de filtro: aunque la API mande un path que no está en `public/`, la
 * app no lo emite y cae en el placeholder. Una imagen rota es peor que un
 * gráfico de marca.
 */
export function esImagenServible(path: string | null | undefined): boolean {
  if (typeof path !== 'string') return false;
  return path.startsWith('/media/') || path.startsWith('/brand/');
}

/**
 * La foto de un producto: la primera de la galería, o el placeholder.
 */
export function imagenDeProducto(producto: Producto): string {
  const primera = producto.imagenes?.find((img) => esImagenServible(img));
  return primera ?? PLACEHOLDER_PRODUCTO;
}

/**
 * La foto de una colección.
 *
 * La manda la API (`coleccion.imagen`): es la foto real de un producto
 * representativo de esa colección. Si no viniera o no fuera servible, se
 * muestra el gráfico de la marca.
 *
 * **La resuelve el propio componente**: no depende de que quien lo usa se
 * acuerde de pasarle el prop `imagen`. Así la home y `/colecciones` muestran
 * siempre la misma foto.
 */
export function imagenDeColeccion(coleccion: Coleccion): string {
  return esImagenServible(coleccion.imagen)
    ? (coleccion.imagen as string)
    : PLACEHOLDER_COLECCION;
}
